<template>
    <vx-expandable-card>
        <template v-slot:header>SSO identities</template>
        <template v-slot:content>
            <page-loading v-if="loading" />

            <vx-alert v-else-if="loadFailed" color="danger" :closeable="false" data-test="load-failed">
                <div class="flex items-center justify-between">
                    The SSO identities could not be loaded.
                    <vx-button size="sm" color="danger" @click="loadIdentities" data-test="retry-load">
                        Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                    </vx-button>
                </div>
            </vx-alert>

            <div v-else-if="!identities.length" class="text-sm text-slate-500" data-test="no-identities">
                No SSO identities.
            </div>

            <div v-else class="flex flex-col divide-y divide-slate-100">
                <div
                    v-for="identity in identities"
                    :key="identity.id"
                    class="flex flex-wrap items-center justify-between gap-3 py-3 text-sm first:pt-0 last:pb-0"
                    data-test="sso-identity"
                >
                    <div class="flex min-w-0 flex-col gap-1">
                        <a :href="`/kiosk/teams/${identity.team_id}`" class="font-bold" data-test="team-link">
                            {{ identity.team_name }}
                        </a>
                        <vx-popover hover arrow placement="top" class="[&>div:first-child]:truncate max-w-xs">
                            <code class="text-xs" data-test="subject">{{ identity.subject }}</code>
                            <template v-slot:content>
                                <div class="max-w-xs break-all">{{ identity.subject }}</div>
                            </template>
                        </vx-popover>
                        <div class="text-xs text-slate-500">
                            Linked {{ shortDateTime(identity.created_at) }} · Last login
                            {{ identity.last_login_at ? shortDateTime(identity.last_login_at) : 'never' }}
                        </div>
                    </div>
                    <vx-button size="sm" color="muted" outline @click="unlink(identity)" data-test="unlink">
                        Unlink <font-awesome-icon widthAuto :icon="faLinkSlash" />
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { faLinkSlash, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxExpandableCard, VxPopover, VxSwal } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { adminSsoClient } from '~/services';
import type { KioskSsoIdentity } from '~/services/adminSsoClient.types';
import { shortDateTime } from '~/utils/date';
import { throwSwalRequestError } from '~/utils/request';

const props = defineProps<{
    userId: number;
}>();

const toasted = useToasted();

const identities = ref<KioskSsoIdentity[]>([]);
const loading = ref(true);
const loadFailed = ref(false);

const loadIdentities = async () => {
    loading.value = true;
    loadFailed.value = false;

    try {
        const { data } = await adminSsoClient.getUserSsoIdentities(props.userId);
        identities.value = data.data;
    } catch {
        loadFailed.value = true;
    } finally {
        loading.value = false;
    }
};

const unlink = (identity: KioskSsoIdentity) => {
    VxSwal.confirmation(
        'Unlink SSO identity',
        'The user will be linked again at their next SSO login if their email still matches a verified domain; if this was their last linked identity, that login claims the account again (password reset, tokens revoked). This is recorded in the audit log. There is no undo.',
        'danger',
        () =>
            adminSsoClient
                .deleteUserSsoIdentity(props.userId, identity.id)
                .then(() => {
                    identities.value = identities.value.filter((candidate) => candidate.id !== identity.id);
                    toasted.success(`${identity.team_name} identity unlinked.`);
                })
                .catch(throwSwalRequestError)
    );
};

onMounted(() => {
    loadIdentities();
});
</script>
